import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Store, Package, ShoppingCart, 
  FileText, Settings, LogOut, Menu, X, TrendingUp, 
  DollarSign, Clock, CheckCircle, AlertCircle, Truck,
  CreditCard, Percent, BarChart3, PieChart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard({ children }) {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    if (user?.user_type !== 'admin') {
      navigate('/');
    }
    fetchStats();
    fetchRecentOrders();
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/orders?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecentOrders(data.orders || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: FileText, label: 'Satıcı Başvuruları', path: '/admin/applications' },
    { icon: Store, label: 'Mağazalar', path: '/admin/stores' },
    { icon: Users, label: 'Kullanıcılar', path: '/admin/users' },
    { icon: Package, label: 'Ürünler', path: '/admin/products' },
    { icon: ShoppingCart, label: 'Siparişler', path: '/admin/orders' },
    { icon: Percent, label: 'Komisyonlar', path: '/admin/commissions' },
    { icon: CreditCard, label: 'Hakedişler', path: '/admin/payouts' },
    { icon: Truck, label: 'Kargo Ayarları', path: '/admin/cargo' },
    { icon: ShieldAlert, label: 'Satıcı İhlalleri', path: '/admin/violations' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-screen transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900/95 backdrop-blur-xl border-r border-gray-700/50`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700/50">
            {sidebarOpen && (
              <Link to="/admin" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <Store className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-bold text-lg">Pazarlo</span>
              </Link>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Menu */}
          <nav className="flex-1 overflow-y-auto py-4 px-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 mb-1 rounded-xl transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25'
                    : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            ))}
          </nav>

          {/* User */}
          <div className="p-4 border-t border-gray-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{user?.name}</p>
                  <p className="text-gray-400 text-sm truncate">{user?.email}</p>
                </div>
              )}
            </div>
            <button
              onClick={() => { logout(); navigate('/'); }}
              className={`mt-3 flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors ${sidebarOpen ? 'w-full' : 'justify-center'}`}
            >
              <LogOut className="w-5 h-5" />
              {sidebarOpen && <span>Çıkış Yap</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="p-6">
          {children || <AdminHome stats={stats} recentOrders={recentOrders} />}
        </div>
      </main>
    </div>
  );
}

function AdminHome({ stats, recentOrders }) {
  if (!stats) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Toplam Kullanıcı', 
      value: stats.total_users || 0, 
      icon: Users,
      gradient: 'from-blue-500 to-blue-600',
      bgGradient: 'from-blue-500/10 to-blue-600/10',
      change: '+12%',
      positive: true
    },
    { 
      label: 'Aktif Satıcı', 
      value: stats.total_sellers || 0, 
      icon: Store,
      gradient: 'from-emerald-500 to-emerald-600',
      bgGradient: 'from-emerald-500/10 to-emerald-600/10',
      change: '+5%',
      positive: true
    },
    { 
      label: 'Toplam Ürün', 
      value: (stats.total_products || 0).toLocaleString(), 
      icon: Package,
      gradient: 'from-purple-500 to-purple-600',
      bgGradient: 'from-purple-500/10 to-purple-600/10',
      change: '+158K',
      positive: true
    },
    { 
      label: 'Bekleyen Sipariş', 
      value: stats.total_orders || 0, 
      icon: ShoppingCart,
      gradient: 'from-amber-500 to-amber-600',
      bgGradient: 'from-amber-500/10 to-amber-600/10',
      change: '28 yeni',
      positive: true
    },
    { 
      label: 'Bekleyen Başvuru', 
      value: stats.pending_applications || 0, 
      icon: FileText,
      gradient: 'from-rose-500 to-rose-600',
      bgGradient: 'from-rose-500/10 to-rose-600/10',
      change: stats.pending_applications > 0 ? 'Acil!' : 'Yok',
      positive: stats.pending_applications === 0
    },
    { 
      label: 'Toplam Komisyon', 
      value: `₺${(stats.total_commission || 0).toLocaleString()}`, 
      icon: DollarSign,
      gradient: 'from-orange-500 to-orange-600',
      bgGradient: 'from-orange-500/10 to-orange-600/10',
      change: '+₺0',
      positive: true
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-gray-400 mt-1">Pazarlo yönetim paneline hoş geldiniz</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-400 text-sm">{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, i) => (
          <div 
            key={i} 
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${stat.bgGradient} border border-gray-700/50 p-6 hover:border-gray-600/50 transition-all duration-300 group`}
          >
            {/* Background Glow */}
            <div className={`absolute -top-12 -right-12 w-32 h-32 bg-gradient-to-br ${stat.gradient} rounded-full opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`} />
            
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-gray-400 text-sm font-medium">{stat.label}</p>
                <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                <div className={`flex items-center gap-1 mt-2 text-sm ${stat.positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {stat.positive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  <span>{stat.change}</span>
                </div>
              </div>
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}>
                <stat.icon className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions & Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-orange-500" />
            Hızlı İşlemler
          </h3>
          <div className="space-y-3">
            <Link 
              to="/admin/orders" 
              className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Siparişleri Gör</p>
                <p className="text-amber-400 text-sm">{stats.total_orders || 0} bekleyen</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-amber-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
            
            <Link 
              to="/admin/applications" 
              className="flex items-center gap-3 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-rose-500 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Başvuruları İncele</p>
                <p className="text-rose-400 text-sm">{stats.pending_applications || 0} bekleyen</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-rose-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
            
            <Link 
              to="/admin/commissions" 
              className="flex items-center gap-3 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                <Percent className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Komisyon Ayarları</p>
                <p className="text-orange-400 text-sm">Oranları düzenle</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-orange-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
            
            <Link 
              to="/admin/payouts" 
              className="flex items-center gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors group"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Hakedişler</p>
                <p className="text-emerald-400 text-sm">Ödemeleri yönet</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-emerald-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              Son Siparişler
            </h3>
            <Link to="/admin/orders" className="text-orange-500 hover:text-orange-400 text-sm font-medium">
              Tümünü Gör →
            </Link>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-gray-400 text-sm border-b border-gray-700/50">
                  <th className="text-left py-3 px-2">Sipariş No</th>
                  <th className="text-left py-3 px-2">Müşteri</th>
                  <th className="text-left py-3 px-2">Tutar</th>
                  <th className="text-left py-3 px-2">Durum</th>
                  <th className="text-left py-3 px-2">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-gray-500">
                      Henüz sipariş yok
                    </td>
                  </tr>
                ) : (
                  recentOrders.map((order, i) => (
                    <tr key={i} className="border-b border-gray-700/30 hover:bg-gray-700/20 transition-colors">
                      <td className="py-3 px-2">
                        <span className="text-orange-500 font-mono text-sm">
                          #{order.order_number?.slice(-8) || order.id?.slice(-8)}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <p className="text-white font-medium text-sm">{order.customer_name || order.shipping_address?.name || '-'}</p>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-white font-semibold">{(order.grand_total || order.total || 0).toLocaleString()} ₺</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                          order.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                          order.status === 'shipped' ? 'bg-purple-500/20 text-purple-400' :
                          order.status === 'delivered' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {order.status === 'pending' ? 'Bekliyor' :
                           order.status === 'confirmed' ? 'Onaylandı' :
                           order.status === 'shipped' ? 'Kargoda' :
                           order.status === 'delivered' ? 'Teslim' : order.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-gray-400 text-sm">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('tr-TR') : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Alert Banner */}
      {stats.pending_applications > 0 && (
        <div className="bg-gradient-to-r from-rose-500/20 to-orange-500/20 border border-rose-500/30 rounded-2xl p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-rose-500 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">Dikkat! {stats.pending_applications} bekleyen satıcı başvurusu var</p>
            <p className="text-gray-300 text-sm">Başvuruları inceleyip onaylayın veya reddedin.</p>
          </div>
          <Link 
            to="/admin/applications" 
            className="px-4 py-2 bg-rose-500 text-white rounded-xl font-medium hover:bg-rose-600 transition-colors"
          >
            İncele
          </Link>
        </div>
      )}
    </div>
  );
}
